import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import {
  DEFAULT_SHARE_CHANNELS,
  SHARE_CHANNELS,
  SHARE_CHANNEL_LABELS,
  PUBLIC_SITE_ORIGIN,
} from "@/lib/share-channels";
import { BellRing, CalendarPlus, Check, Copy, HelpCircle, Link2, Plus, Trash2, X } from "lucide-react";
import { EventQrCode } from "@/components/app/event-qr-code";
import { EventAnalytics } from "@/components/app/event-analytics";
import { EventRoster } from "@/components/app/event-roster";
import { EventVolunteers } from "@/components/app/event-volunteers";
import { EventScheduleGrid } from "@/components/app/event-schedule-grid";
import { ShareImageUpload } from "@/components/app/share-image-upload";
import {
  CapacityPips,
  CoverageBar,
  EventCoverage,
  eventFill,

  FillTag,
  dateFill,
  slotFill,
} from "@/components/app/event-coverage";


import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/app/rich-text-editor";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { adminEventsQuery, programsAuthedQuery } from "@/lib/queries";
import { adminGetEventDefaults } from "@/lib/settings.functions";
import { formatTime } from "@/routes/events.index";
import { T1D_RELATIONSHIPS, REFERRAL_SOURCES } from "@/lib/t1d-relationships";
import {
  CLAIM_FIELD_TYPE_LABELS,
  EMPTY_CLAIM_CONFIG,
  MAX_CUSTOM_FIELDS,
  SYSTEM_CLAIM_GROUPS,
  normalizeClaimFieldConfig,
  resolveClaimFields,
  slugifyFieldKey,
  type ClaimField,
  type ClaimFieldConfig,
  type ClaimSystemSetting,
} from "@/lib/claim-fields";
import {

  adminAddEventDate,
  adminAddSignup,
  adminBulkAddDates,
  adminBulkCopySlots,
  adminAnnounceEventToVolunteers,
  adminBulkDeleteDates,
  adminBulkDeleteSlots,
  adminBulkShiftDates,
  adminBulkUpdateDates,
  adminBulkUpdateSlots,
  adminCancelSignup,
  adminDeleteEvent,
  adminDeleteEventDate,
  adminDeleteSlot,
  adminDuplicateEventDate,
  adminDuplicateSlot,
  adminRunShiftReminders,
  adminSaveEvent,
  adminSaveSlot,
  adminUpdateEventDate,
  adminUpdateSignup,
} from "@/lib/event-admin.functions";
import { reportHandledSync } from "@/lib/observability";
import { flow } from "@/lib/flow";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type DetailTab = "overview" | "schedule" | "roster" | "promote" | "analytics";

const DETAIL_TABS: [DetailTab, string][] = [
  ["overview", "Overview"],
  ["schedule", "Dates and shifts"],
  ["roster", "Roster"],
  ["promote", "Share and QR"],
  ["analytics", "Analytics"],
];


export const Route = createFileRoute("/_authenticated/t1dwpanel/events")({
  head: () => ({
    meta: [
      { title: "Event Manager | Warrior Volunteer" },
      { name: "description", content: "Manage volunteer events, dates, and shift slots." },
      { property: "og:title", content: "Event Manager | Warrior Volunteer" },
      { property: "og:description", content: "Manage volunteer events, dates, and shift slots." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminEventsPage,
});

type AnyRow = Record<string, any>;

function publicEventUrl(slug: string) {
  // Always share the live production link, never a preview host.
  return `${PUBLIC_SITE_ORIGIN}/events/${slug}`;
}

async function writeClipboard(text: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

function AnnounceEventButton({ event, onDone }: { event: AnyRow; onDone: () => void }) {
  const announce = useServerFn(adminAnnounceEventToVolunteers);
  const [busy, setBusy] = useState(false);
  const alreadySent = Boolean(event.volunteer_announced_at);

  return (
    <Button
      type="button"
      variant="outline"
      disabled={busy}
      onClick={async () => {
        if (
          alreadySent &&
          !confirm("Volunteers were already emailed about this event. Send it again?")
        )
          return;
        setBusy(true);
        try {
          const result = await announce({
            data: { id: event.id, origin: window.location.origin, force: alreadySent },
          });
          if (result.sent > 0) {
            toast.success(`Emailed ${result.sent} volunteer${result.sent === 1 ? "" : "s"}.`);
          } else if (result.skipped === "no_spots") {
            toast.error("Add dates and open spots before announcing this event.");
          } else if (result.skipped === "not_published") {
            toast.error("Publish the event first.");
          } else if (result.skipped === "disabled") {
            toast.error("Volunteer announcements are turned off in notification settings.");
          } else if (result.skipped === "no_recipients") {
            toast.error("No active volunteers have opted into email yet.");
          } else {
            toast.error("Nothing was sent. Check notification settings.");
          }
          onDone();
        } catch (error) {
          reportHandledSync(error, { feature: "admin", action: "announceEvent" });
          toast.error(error instanceof Error ? error.message : "Could not send the announcement.");
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? "Sending..." : alreadySent ? "Announce again" : "Announce to volunteers"}
    </Button>
  );
}

function CopyEventLink({ event }: { event: AnyRow }) {
  const [copied, setCopied] = useState(false);


  return (
    <Button
      type="button"
      variant="outline"
      onClick={async () => {
        const url = publicEventUrl(event.slug);
        const ok = await writeClipboard(url);
        if (!ok) {
          toast.error("Could not copy the link. Copy it from the address below the title.");
          return;
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success(
          event.is_published
            ? "Link copied."
            : "Link copied. Heads up, this event is still a draft so the page is not live yet.",
        );
      }}
    >
      {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      <span className="ml-2">Copy link</span>
    </Button>
  );
}

function dayLabel(value: string) {
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

function FieldLabel({
  htmlFor,
  children,
  help,
}: {
  htmlFor: string;
  children: React.ReactNode;
  help: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{children}</Label>
      <TooltipProvider delayDuration={120}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`What goes in ${String(children)}`}
              className="text-muted-foreground transition-colors hover:text-primary"
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-[260px] text-xs leading-relaxed">
            {help}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

const ClaimFieldsContext = createContext<ClaimField[]>([]);

/** Inputs for the event's extra claim questions inside the admin add-to-slot form. */
function AdminClaimFieldInputs({ slotId }: { slotId: string }) {
  const fields = useContext(ClaimFieldsContext);
  if (fields.length === 0) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {fields.map((field) => {
        const id = `cf-${field.key}-${slotId}`;
        return (
          <div key={field.key} className="space-y-2">
            <FieldLabel htmlFor={id} help={field.help ?? "Extra question set up on this event."}>
              {field.label}
            </FieldLabel>
            {field.type === "select" ? (
              <select
                id={id}
                name={`cf_${field.key}`}
                defaultValue=""
                className="h-11 w-full rounded-sm border border-input bg-background px-3 text-sm"
              >
                <option value="">Not provided</option>
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.type === "checkbox" ? (
              <label className="flex h-11 items-center gap-2 text-sm text-ice/85">
                <input
                  id={id}
                  name={`cf_${field.key}`}
                  type="checkbox"
                  className="h-4 w-4 accent-cyan"
                />
                Yes
              </label>
            ) : (
              <Input id={id} name={`cf_${field.key}`} maxLength={1000} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ClaimQuestionsEditor({
  config,
  onChange,
}: {
  config: ClaimFieldConfig;
  onChange: (next: ClaimFieldConfig) => void;
}) {
  function setSystem(key: string, patch: Partial<ClaimSystemSetting>) {
    const current = config.system[key] ?? { enabled: false, required: false };
    onChange({ ...config, system: { ...config.system, [key]: { ...current, ...patch } } });
  }

  function setCustom(index: number, patch: Partial<ClaimField>) {
    const custom = config.custom.map((field, i) => (i === index ? { ...field, ...patch } : field));
    onChange({ ...config, custom });
  }

  return (
    <div className="space-y-4 rounded-sm border border-border p-3">
      <FieldLabel
        htmlFor="claim-questions"
        help="Extra questions asked when someone claims a spot on this event. Turn on the built-in ones or add your own."
      >
        Claim questions
      </FieldLabel>

      <div className="space-y-2" id="claim-questions">
        {SYSTEM_CLAIM_GROUPS.map((group) => {
          const first = group.fields[0].key;
          const setting = config.system[first] ?? { enabled: false, required: false };
          return (
            <div key={group.key} className="space-y-1 rounded-sm border border-border/70 p-3">
              <label className="flex items-center gap-2 text-sm text-ice/85">
                <input
                  type="checkbox"
                  checked={setting.enabled}
                  onChange={(e) => {
                    const enabled = e.currentTarget.checked;
                    const system = { ...config.system };
                    for (const field of group.fields) {
                      system[field.key] = { enabled, required: enabled && setting.required };
                    }
                    onChange({ ...config, system });
                  }}
                  className="h-4 w-4 accent-cyan"
                />
                Ask for {group.label.toLowerCase()}
              </label>
              <p className="t-meta">{group.help}</p>
              {setting.enabled ? (
                <label className="flex items-center gap-2 text-sm text-ice/85">
                  <input
                    type="checkbox"
                    checked={setting.required}
                    onChange={(e) => {
                      const required = e.currentTarget.checked;
                      const system = { ...config.system };
                      for (const field of group.fields) {
                        system[field.key] = { enabled: true, required };
                      }
                      onChange({ ...config, system });
                    }}
                    className="h-4 w-4 accent-cyan"
                  />
                  Required
                </label>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="space-y-3">
        {config.custom.map((field, index) => (
          <div key={index} className="space-y-2 rounded-sm border border-border/70 p-3">
            <div className="grid gap-2 sm:grid-cols-[1fr_180px]">
              <Input
                value={field.label}
                placeholder="Question shown to the claimer"
                onChange={(e) =>
                  setCustom(index, {
                    label: e.currentTarget.value,
                    key: field.key || slugifyFieldKey(e.currentTarget.value),
                  })
                }
              />
              <select
                value={field.type}
                onChange={(e) =>
                  setCustom(index, { type: e.currentTarget.value as ClaimField["type"] })
                }
                className="h-10 rounded-sm border border-input bg-background px-3 text-sm"
              >
                {Object.entries(CLAIM_FIELD_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {field.type === "select" ? (
              <Input
                value={field.options.join(", ")}
                placeholder="Choices, separated by commas"
                onChange={(e) =>
                  setCustom(index, {
                    options: e.currentTarget.value
                      .split(",")
                      .map((option) => option.trim())
                      .filter(Boolean),
                  })
                }
              />
            ) : null}
            <Input
              value={field.help ?? ""}
              placeholder="Helper text (optional)"
              onChange={(e) => setCustom(index, { help: e.currentTarget.value })}
            />
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-ice/85">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => setCustom(index, { required: e.currentTarget.checked })}
                  className="h-4 w-4 accent-cyan"
                />
                Required
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  onChange({ ...config, custom: config.custom.filter((_, i) => i !== index) })
                }
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      {config.custom.length < MAX_CUSTOM_FIELDS ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({
              ...config,
              custom: [
                ...config.custom,
                {
                  key: "",
                  label: "",
                  type: "text",
                  options: [],
                  help: null,
                  required: false,
                  system: false,
                },
              ],
            })
          }
        >
          Add question
        </Button>
      ) : null}
    </div>
  );
}

function AdminEventsPage() {
  const { data: events = [], isLoading } = useQuery(adminEventsQuery);
  const { data: programs = [] } = useQuery(programsAuthedQuery);
  const loadEventDefaults = useServerFn(adminGetEventDefaults);
  const { data: eventDefaults } = useQuery({
    queryKey: ["admin", "event-defaults"],
    queryFn: () => loadEventDefaults(),
  });
  const queryClient = useQueryClient();
  const saveEvent = useServerFn(adminSaveEvent);
  const deleteEvent = useServerFn(adminDeleteEvent);
  const [editing, setEditing] = useState<AnyRow | null>(null);
  const [titleValue, setTitleValue] = useState("");
  const [slugValue, setSlugValue] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [claimConfig, setClaimConfig] = useState<ClaimFieldConfig>(EMPTY_CLAIM_CONFIG);
  const [tab, setTab] = useState<"events" | "people">("events");
  const [openId, setOpenId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>("overview");
  const [search, setSearch] = useState("");

  const selected = openId ? (events.find((e: AnyRow) => e.id === openId) ?? null) : null;
  const term = search.trim().toLowerCase();
  const filtered = term
    ? events.filter((e: AnyRow) =>
        `${e.title ?? ""} ${e.slug ?? ""} ${e.location_name ?? ""}`.toLowerCase().includes(term),
      )
    : events;


  function openEditor(row: AnyRow | null) {
    setEditing(row);
    setClaimConfig(normalizeClaimFieldConfig((row as any)?.claim_field_config));
    setTitleValue(row?.title ?? "");
    setSlugValue(row?.slug ?? "");
    setSlugEdited(Boolean(row?.id));
  }


  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin", "events"] });

  async function submitEvent(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    flow(editing ? "admin.event.edit" : "admin.event.create", { eventId: editing?.id ?? null });
    try {
      await saveEvent({
        data: {
          id: editing?.id,
          title: String(form.get("title") ?? ""),
          slug: String(form.get("slug") ?? ""),
          program_id: String(form.get("program_id") ?? "") || null,
          description: String(form.get("description") ?? ""),
          location_name: String(form.get("location_name") ?? ""),
          address: String(form.get("address") ?? ""),
          contact_name: String(form.get("contact_name") ?? ""),
          contact_email: String(form.get("contact_email") ?? ""),
          contact_phone: String(form.get("contact_phone") ?? ""),
          public_contact_name: String(form.get("public_contact_name") ?? ""),
          public_contact_email: String(form.get("public_contact_email") ?? ""),
          public_contact_phone: String(form.get("public_contact_phone") ?? ""),
          notify_emails: String(form.get("notify_emails") ?? ""),
          share_enabled: form.get("share_enabled") === "on",
          share_channels: form.getAll("share_channels").map(String),
          share_message: String(form.get("share_message") ?? ""),
          share_image_url: String(form.get("share_image_url") ?? ""),
          is_published: form.get("is_published") === "on",
          reminder_7day: form.get("reminder_7day") === "on",
          reminder_1day: form.get("reminder_1day") === "on",
          claim_field_config: normalizeClaimFieldConfig(claimConfig),

        },
      });
      toast.success("Event saved.");
      setEditing(null);
      refresh();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submitEvent" });
      toast.error(error instanceof Error ? error.message : "Could not save that event.");
    }
  }

  return (
    <AppShell
      eyebrow="Admin"
      title="Events"
      actions={
        tab === "events" ? (
          <Button
            onClick={() => openEditor({ is_published: false, ...(eventDefaults ?? {}) })}
            type="button"
          >
            <CalendarPlus className="mr-2 h-4 w-4" /> New event
          </Button>
        ) : null
      }
    >
      <div className="mb-6 flex gap-2 border-b border-border">
        {(
          [
            ["events", "Events"],
            ["people", "Event helpers"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "people" ? <EventVolunteers /> : null}

      {tab === "events" && editing ? (
        <form onSubmit={submitEvent} className="panel mb-8 space-y-5 p-6">
          <div className="flex items-center justify-between">
            <h2 className="t-h3 text-ice">{editing.id ? "Edit event" : "New event"}</h2>
            <button type="button" onClick={() => setEditing(null)} aria-label="Close">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <FieldLabel
                htmlFor="title"
                help="The public name of the event, exactly how volunteers should see it on the events page. Example: VOA Country Music Festival."
              >
                Event title
              </FieldLabel>
              <Input
                id="title"
                name="title"
                value={titleValue}
                onChange={(e) => {
                  const next = e.target.value;
                  setTitleValue(next);
                  if (!slugEdited) setSlugValue(slugify(next));
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="slug"
                help="The web address for this event. It fills in automatically from the title and you can edit it. Use lowercase words with dashes. The page will live at /events/your-slug."
              >
                Web address slug
              </FieldLabel>
              <Input
                id="slug"
                name="slug"
                value={slugValue}
                onChange={(e) => {
                  setSlugEdited(true);
                  setSlugValue(e.target.value);
                }}
                onBlur={(e) => setSlugValue(slugify(e.target.value))}
                placeholder="boone-county-classic"
                required
              />
              <p className="text-xs text-muted-foreground">/events/{slugValue || "your-event"}</p>
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="program_id"
                help="Which T1D Warriors program this event belongs to. Pick In Person Events if it does not roll up to a standing program."
              >
                Program
              </FieldLabel>
              <select
                id="program_id"
                name="program_id"
                defaultValue={editing.program_id ?? ""}
                className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
              >
                <option value="">No program</option>
                {programs.map((program: AnyRow) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="location_name"
                help="The venue name volunteers will recognize. Example: Boone County Golf Club or VOA Park."
              >
                Location name
              </FieldLabel>
              <Input
                id="location_name"
                name="location_name"
                defaultValue={editing.location_name ?? ""}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="address"
                help="Full street address so volunteers can drop it straight into maps. Example: 6019 Limaburg Rd, Burlington, KY 41005."
              >
                Address
              </FieldLabel>
              <Input id="address" name="address" defaultValue={editing.address ?? ""} />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="contact_name"
                help="Internal only. Who runs point on the day of the event. This is not shown to the public."
              >
                Internal contact name
              </FieldLabel>
              <Input id="contact_name" name="contact_name" defaultValue={editing.contact_name ?? ""} />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="contact_email"
                help="Internal only. Staff email for this event. Not shown on the public event page."
              >
                Internal contact email
              </FieldLabel>
              <Input
                id="contact_email"
                name="contact_email"
                type="email"
                defaultValue={editing.contact_email ?? ""}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="contact_phone"
                help="Internal only. Staff phone for this event. Not shown on the public event page."
              >
                Internal contact phone
              </FieldLabel>
              <Input
                id="contact_phone"
                name="contact_phone"
                type="tel"
                placeholder="859 555 0134"
                defaultValue={editing.contact_phone ?? ""}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="public_contact_name"
                help="Shown publicly. The name volunteers should ask for with questions about this event."
              >
                Public contact name
              </FieldLabel>
              <Input
                id="public_contact_name"
                name="public_contact_name"
                defaultValue={editing.public_contact_name ?? ""}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="public_contact_email"
                help="Shown publicly on the event page and in confirmation emails. Leave blank to hide it."
              >
                Public contact email
              </FieldLabel>
              <Input
                id="public_contact_email"
                name="public_contact_email"
                type="email"
                defaultValue={editing.public_contact_email ?? ""}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="public_contact_phone"
                help="Shown publicly on the event page. Include the area code, for example 859 555 0134. Leave blank to hide it."
              >
                Public contact phone
              </FieldLabel>
              <Input
                id="public_contact_phone"
                name="public_contact_phone"
                type="tel"
                placeholder="859 555 0134"
                defaultValue={editing.public_contact_phone ?? ""}
              />
            </div>
          </div>
          <div className="space-y-2">
            <FieldLabel
              htmlFor="notify_emails"
              help="Only these addresses get an alert email when someone claims a spot. Separate multiple addresses with commas. Leave blank to send no alerts."
            >
              Notify on signup
            </FieldLabel>
            <Input
              id="notify_emails"
              name="notify_emails"
              placeholder="lead@t1dw.org, crew@t1dw.org"
              defaultValue={((editing as any).notify_emails ?? []).join(", ")}
            />
          </div>
          <div className="space-y-3 border border-border p-4" style={{ borderRadius: 4 }}>
            <div className="flex items-center gap-2">
              <input
                id="share_enabled"
                name="share_enabled"
                type="checkbox"
                defaultChecked={(editing as any).share_enabled !== false}
                className="h-4 w-4"
              />
              <FieldLabel
                htmlFor="share_enabled"
                help="Turns the share button on the public event page and the share block in confirmation emails on or off."
              >
                Let people share this event
              </FieldLabel>
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="share_channels"
                help="Pick which share options show up. Text message copies the message on desktop and opens the texting app on phones."
              >
                Share options
              </FieldLabel>
              <div className="flex flex-wrap gap-3">
                {SHARE_CHANNELS.map((channel) => (
                  <label key={channel} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      id={`share_channels${channel === SHARE_CHANNELS[0] ? "" : `-${channel}`}`}
                      name="share_channels"
                      value={channel}
                      defaultChecked={(
                        ((editing as any).share_channels as string[] | null) ?? DEFAULT_SHARE_CHANNELS
                      ).includes(channel)}
                      className="h-4 w-4"
                    />
                    {SHARE_CHANNEL_LABELS[channel]}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="share_message"
                help="The wording that gets pre-filled in texts, posts, and emails. Use {url} where you want the event link to land. Leave blank for the default."
              >
                Share message
              </FieldLabel>
              <Input
                id="share_message"
                name="share_message"
                placeholder="Come volunteer with us at the VOA festival. Grab a spot: {url}"
                defaultValue={(editing as any).share_message ?? ""}
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                htmlFor="share_image_url"
                help="The picture that shows when this event is shared on Facebook, X, LinkedIn, or in a text. Wide images around 1200 by 630 look best. Max 5 MB."
              >
                Share image
              </FieldLabel>
              <ShareImageUpload
                name="share_image_url"
                eventId={editing.id ?? "new"}
                defaultValue={(editing as any).share_image_url ?? ""}
              />
            </div>

          </div>
          <div className="space-y-2">
            <FieldLabel
              htmlFor="description"
              help="A short paragraph on what the event is, what volunteers will actually be doing, and anything to bring or wear. Use the toolbar for bold, headings, lists, and links."
            >
              Description
            </FieldLabel>
            <RichTextEditor
              id="description"
              name="description"
              defaultValue={editing.description ?? ""}
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-ice/85">
            <input
              type="checkbox"
              name="is_published"
              defaultChecked={editing.is_published ?? false}
              disabled={!editing.id}
              className="mt-1 h-4 w-4 accent-cyan"
            />
            <span>
              Published on the public events page
              <span className="mt-1 block text-xs text-ice/55">
                {editing.id
                  ? "Publishing sends the internal alert and the volunteer announcement."
                  : "New events save as a draft. Add dates and shifts, then publish to send the emails."}
              </span>
            </span>
          </label>


          <div className="space-y-2 rounded-sm border border-border p-3">
            <p className="text-sm font-bold uppercase tracking-wide text-ice">Shift reminders</p>
            <label className="flex items-center gap-2 text-sm text-ice/85">
              <input
                type="checkbox"
                name="reminder_7day"
                defaultChecked={(editing as any).reminder_7day ?? true}
                className="h-4 w-4 accent-cyan"
              />
              Email everyone 7 days before their shift
            </label>
            <label className="flex items-center gap-2 text-sm text-ice/85">
              <input
                type="checkbox"
                name="reminder_1day"
                defaultChecked={(editing as any).reminder_1day ?? true}
                className="h-4 w-4 accent-cyan"
              />
              Email everyone the day before their shift
            </label>
          </div>
          <ClaimQuestionsEditor config={claimConfig} onChange={setClaimConfig} />

          <Button type="submit" >
            Save event
          </Button>
        </form>
      ) : null}

      {tab === "events" && isLoading ? <p className="t-meta">Loading events...</p> : null}
      {tab === "events" && !isLoading && events.length === 0 ? (
        <p className="t-meta">No events yet. Create one to start collecting signups.</p>
      ) : null}

      {tab === "events" && !editing && !selected ? (
        <div className="panel overflow-hidden">
          <div className="border-b border-border p-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events"
              className="h-9 max-w-sm"
            />
          </div>
          <div className="hidden grid-cols-[minmax(0,2.5fr)_1fr_1fr_1.4fr_auto] gap-3 border-b border-border px-4 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground md:grid">
            <span>Event</span>
            <span>Status</span>
            <span>Dates</span>
            <span>Coverage</span>
            <span className="sr-only">Open</span>
          </div>
          {filtered.length === 0 ? (
            <p className="t-meta p-4">No events match that search.</p>
          ) : null}
          {filtered.map((event: AnyRow) => {
            const fill = eventFill(event);
            const dates = [...((event.event_dates ?? []) as AnyRow[])]
              .map((d) => String(d.event_date))
              .sort();
            return (
              <button
                key={event.id}
                type="button"
                onClick={() => {
                  setOpenId(event.id);
                  setDetailTab("overview");
                }}
                className="grid w-full grid-cols-1 items-center gap-2 border-b border-border px-4 py-3 text-left last:border-b-0 hover:bg-muted/50 md:grid-cols-[minmax(0,2.5fr)_1fr_1fr_1.4fr_auto] md:gap-3"
              >
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-ice">{event.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {event.programs?.name ?? "No program"} · /events/{event.slug}
                  </span>
                </span>
                <span
                  className={`justify-self-start rounded-sm px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${
                    event.is_published
                      ? "bg-cyan/15 text-cyan"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {event.is_published ? "Published" : "Draft"}
                </span>
                <span className="text-sm text-muted-foreground">
                  {dates.length === 0
                    ? "No dates"
                    : dates.length === 1
                      ? dayLabel(dates[0]!)
                      : `${dayLabel(dates[0]!)} to ${dayLabel(dates[dates.length - 1]!)}`}
                </span>
                <span className="min-w-0">
                  <CoverageBar fill={fill} />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {fill.filled} of {fill.capacity} claimed
                  </span>
                </span>
                <span className="text-xs font-bold uppercase tracking-wide text-primary">Open</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {tab === "events" && !editing && selected ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => setOpenId(null)}
                className="text-xs font-bold uppercase tracking-wide text-muted-foreground hover:text-primary"
              >
                &larr; All events
              </button>
              <p className="t-eyebrow mt-2">
                {selected.programs?.name ?? "No program"} ·{" "}
                {selected.is_published ? "Published" : "Draft"}
              </p>
              <h2 className="t-h3 mt-1 text-ice">{selected.title}</h2>
              <a
                className="t-meta mt-1 inline-block underline underline-offset-4 hover:text-primary"
                href={`/events/${selected.slug}`}
                target="_blank"
                rel="noreferrer"
              >
                /events/{selected.slug}
              </a>
            </div>
            <div className="flex flex-wrap gap-2">
              <CopyEventLink event={selected} />
              <AnnounceEventButton event={selected} onDone={refresh} />
              <Button type="button" variant="outline" onClick={() => openEditor(selected)}>
                Edit
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  if (!confirm("Delete this event and every signup on it?")) return;
                  try {
                    await deleteEvent({ data: { id: selected.id } });
                    toast.success("Event deleted.");
                    setOpenId(null);
                    refresh();
                  } catch (error) {
                    reportHandledSync(error, { feature: "admin", action: "submitEvent" });
                    toast.error(
                      error instanceof Error ? error.message : "Could not delete that event.",
                    );
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 border-b border-border">
            {DETAIL_TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setDetailTab(key)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${
                  detailTab === key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {detailTab === "overview" ? (
            <div className="space-y-6">
              <EventCoverage event={selected} />
              <EventReminders event={selected} onChange={refresh} />
            </div>
          ) : null}

          {detailTab === "schedule" ? (
            <div className="space-y-6">
              <EventScheduleGrid event={selected} />
              <EventDates event={selected} onChange={refresh} />
            </div>
          ) : null}

          {detailTab === "roster" ? <EventRoster event={selected} onChange={refresh} /> : null}

          {detailTab === "promote" ? (
            <EventQrCode
              slug={selected.slug}
              title={selected.title}
              url={publicEventUrl(selected.slug)}
            />
          ) : null}

          {detailTab === "analytics" ? (
            <EventAnalytics eventId={selected.id} title={selected.title} />
          ) : null}
        </div>
      ) : null}

    </AppShell>
  );
}

function EventReminders({ event, onChange }: { event: AnyRow; onChange: () => void }) {
  const runReminders = useServerFn(adminRunShiftReminders);
  const [busy, setBusy] = useState(false);

  const lastRun = event.reminders_last_run_at
    ? new Date(event.reminders_last_run_at).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="rounded-sm border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-ice">Shift reminders</p>
          <p className="t-meta mt-1">
            {event.reminder_7day === false && event.reminder_1day === false
              ? "Reminders are turned off for this event."
              : `Sending ${[
                  event.reminder_7day !== false ? "7 days before" : null,
                  event.reminder_1day !== false ? "1 day before" : null,
                ]
                  .filter(Boolean)
                  .join(" and ")}. Change this in Edit.`}
          </p>
          <p className="t-meta mt-1">
            {lastRun ? `Last run ${lastRun}.` : "No reminders sent yet."}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const result = await runReminders({
                data: {
                  event_id: event.id,
                  origin: typeof window !== "undefined" ? window.location.origin : undefined,
                },
              });
              toast.success(
                result.sent > 0
                  ? `Sent ${result.sent} reminder${result.sent === 1 ? "" : "s"}.`
                  : "Nothing due right now. Reminders go out 7 days and 1 day before each shift.",
              );
              onChange();
            } catch (error) {
              reportHandledSync(error, { feature: "admin", action: "EventReminders" });
              toast.error(
                error instanceof Error ? error.message : "Could not run reminders just now.",
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          <BellRing className="mr-1.5 h-4 w-4" /> Send reminders now
        </Button>
      </div>
    </div>
  );
}

function EventDates({ event, onChange }: { event: AnyRow; onChange: () => void }) {
  const addDate = useServerFn(adminAddEventDate);
  const bulkAddDates = useServerFn(adminBulkAddDates);
  const bulkShiftDates = useServerFn(adminBulkShiftDates);
  const bulkUpdateDates = useServerFn(adminBulkUpdateDates);
  const bulkDeleteDates = useServerFn(adminBulkDeleteDates);
  const bulkUpdateSlots = useServerFn(adminBulkUpdateSlots);
  const bulkDeleteSlots = useServerFn(adminBulkDeleteSlots);
  const bulkCopySlots = useServerFn(adminBulkCopySlots);

  const [newDate, setNewDate] = useState("");
  const [showRange, setShowRange] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [gapsOnly, setGapsOnly] = useState(false);

  const dates = [...(event.event_dates ?? [])].sort((a: AnyRow, b: AnyRow) =>
    String(a.event_date).localeCompare(String(b.event_date)),
  );
  const allSlots = dates.flatMap((d: AnyRow) => d.event_slots ?? []);

  function toggle(list: string[], setList: (next: string[]) => void, id: string) {
    setList(list.includes(id) ? list.filter((v) => v !== id) : [...list, id]);
  }

  async function run(label: string, action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      toast.success(label);
      setSelectedDates([]);
      setSelectedSlots([]);
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "run" });
      toast.error(error instanceof Error ? error.message : "That bulk action did not go through.");
    } finally {
      setBusy(false);
    }
  }

  async function submitRange(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const weekdays = form.getAll("weekdays").map((v) => Number(v));
    await run("Dates added.", async () => {
      const result = await bulkAddDates({
        data: {
          event_id: event.id,
          start_date: String(form.get("start_date") ?? ""),
          end_date: String(form.get("end_date") ?? ""),
          weekdays,
          note: String(form.get("note") ?? ""),
          copy_slots_from_date_id: String(form.get("copy_from") ?? "") || null,
        },
      });
      setShowRange(false);
      return result;
    });
  }

  return (
    <div className="mt-6 space-y-5 border-t border-border pt-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-2">
          <FieldLabel htmlFor={`date-${event.id}`} help="Pick a calendar day this event runs. Add one date per day, then add the shift slots under it.">Add a date</FieldLabel>
          <Input
            id={`date-${event.id}`}
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="w-[190px]"
          />
        </div>
        <Button
          type="button"
          onClick={async () => {
            if (!newDate) return;
            try {
              await addDate({ data: { event_id: event.id, event_date: newDate } });
              setNewDate("");
              onChange();
            } catch (error) {
              reportHandledSync(error, { feature: "admin", action: "submitRange" });
              toast.error(error instanceof Error ? error.message : "Could not add that date.");
            }
          }}
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add date
        </Button>
        <Button type="button" variant="outline" onClick={() => setShowRange(!showRange)}>
          <CalendarPlus className="mr-1.5 h-4 w-4" /> Add a range of dates
        </Button>
        <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm font-semibold text-ice">
          <input
            type="checkbox"
            className="h-4 w-4 accent-cyan"
            checked={gapsOnly}
            onChange={(e) => setGapsOnly(e.target.checked)}
          />
          Show only slots that still need people
        </label>
      </div>

      {showRange ? (
        <form onSubmit={submitRange} className="grid gap-3 rounded-sm border border-border p-3 sm:grid-cols-3">
          <div className="space-y-2">
            <FieldLabel htmlFor={`range-start-${event.id}`} help="First day in the range.">Start date</FieldLabel>
            <Input id={`range-start-${event.id}`} name="start_date" type="date" required />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`range-end-${event.id}`} help="Last day in the range. Days that already exist are skipped.">End date</FieldLabel>
            <Input id={`range-end-${event.id}`} name="end_date" type="date" required />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`range-copy-${event.id}`} help="Optional. Copy every slot from an existing day onto each new day.">Copy slots from</FieldLabel>
            <select
              id={`range-copy-${event.id}`}
              name="copy_from"
              defaultValue=""
              className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
            >
              <option value="">Do not copy slots</option>
              {dates.map((d: AnyRow) => (
                <option key={d.id} value={d.id}>
                  {dayLabel(d.event_date)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2 sm:col-span-3">
            <FieldLabel htmlFor={`range-note-${event.id}`} help="Optional note applied to every new day.">Day note</FieldLabel>
            <Input id={`range-note-${event.id}`} name="note" />
          </div>
          <div className="sm:col-span-3">
            <p className="t-meta mb-2">Only these days of the week. Leave all unchecked for every day.</p>
            <div className="flex flex-wrap gap-3">
              {WEEKDAYS.map((label, index) => (
                <label key={label} className="flex items-center gap-1.5 text-sm text-ice/80">
                  <input type="checkbox" name="weekdays" value={index} className="h-4 w-4" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2 sm:col-span-3">
            <Button type="submit" disabled={busy}>Create dates</Button>
            <Button type="button" variant="outline" onClick={() => setShowRange(false)}>Cancel</Button>
          </div>
        </form>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-cyan">
        <button type="button" onClick={() => setSelectedDates(dates.map((d: AnyRow) => d.id))}>
          Select all dates
        </button>
        <button type="button" onClick={() => setSelectedSlots(allSlots.map((s: AnyRow) => s.id))}>
          Select all slots
        </button>
        {selectedDates.length + selectedSlots.length > 0 ? (
          <button
            type="button"
            className="text-muted-foreground"
            onClick={() => {
              setSelectedDates([]);
              setSelectedSlots([]);
            }}
          >
            Clear selection
          </button>
        ) : null}
      </div>

      {selectedDates.length > 0 ? (
        <form
          className="grid gap-3 rounded-sm border border-cyan/50 bg-cyan/5 p-3 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            const days = Number(form.get("days") ?? 0);
            const note = String(form.get("note") ?? "");
            void run("Dates updated.", async () => {
              if (days) await bulkShiftDates({ data: { ids: selectedDates, days } });
              if (note.trim()) await bulkUpdateDates({ data: { ids: selectedDates, note } });
              if (!days && !note.trim()) throw new Error("Enter a day shift or a note to apply.");
            });
          }}
        >
          <p className="text-sm font-bold text-ice sm:col-span-4">
            {selectedDates.length} date{selectedDates.length > 1 ? "s" : ""} selected
          </p>
          <div className="space-y-2">
            <FieldLabel htmlFor={`bulk-days-${event.id}`} help="Move every selected day forward or back. Use a negative number to move earlier.">Move by days</FieldLabel>
            <Input id={`bulk-days-${event.id}`} name="days" type="number" placeholder="0" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <FieldLabel htmlFor={`bulk-note-${event.id}`} help="Replace the day note on every selected date.">Set day note</FieldLabel>
            <Input id={`bulk-note-${event.id}`} name="note" />
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit" disabled={busy}>Apply</Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                if (!confirm(`Delete ${selectedDates.length} dates and all their slots?`)) return;
                void run("Dates deleted.", () => bulkDeleteDates({ data: { ids: selectedDates } }));
              }}
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete
            </Button>
          </div>
        </form>
      ) : null}

      {selectedSlots.length > 0 ? (
        <form
          className="grid gap-3 rounded-sm border border-cyan/50 bg-cyan/5 p-3 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            void run("Slots updated.", () =>
              bulkUpdateSlots({
                data: {
                  ids: selectedSlots,
                  title: String(form.get("title") ?? ""),
                  slot_type: String(form.get("slot_type") ?? ""),
                  start_time: String(form.get("start_time") ?? ""),
                  end_time: String(form.get("end_time") ?? ""),
                  capacity: Number(form.get("capacity") ?? 0),
                  notes: String(form.get("notes") ?? ""),
                  shift_minutes: Number(form.get("shift_minutes") ?? 0),
                },
              }),
            );
          }}
        >
          <p className="text-sm font-bold text-ice sm:col-span-4">
            {selectedSlots.length} slot{selectedSlots.length > 1 ? "s" : ""} selected. Leave a field
            blank to keep what is there.
          </p>
          <div className="space-y-2">
            <FieldLabel htmlFor={`bs-cap-${event.id}`} help="Set the same number of spots on every selected slot.">Spots needed</FieldLabel>
            <Input id={`bs-cap-${event.id}`} name="capacity" type="number" min={1} placeholder="Keep" />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`bs-type-${event.id}`} help="Change every selected slot to a shift or an item to bring.">Type</FieldLabel>
            <select
              id={`bs-type-${event.id}`}
              name="slot_type"
              defaultValue=""
              className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
            >
              <option value="">Keep</option>
              <option value="shift">Shift</option>
              <option value="item">Item to bring</option>
            </select>
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`bs-start-${event.id}`} help="Set the same start time on every selected slot.">Start</FieldLabel>
            <Input id={`bs-start-${event.id}`} name="start_time" type="time" />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`bs-end-${event.id}`} help="Set the same end time on every selected slot.">End</FieldLabel>
            <Input id={`bs-end-${event.id}`} name="end_time" type="time" />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`bs-shift-${event.id}`} help="Slide existing start and end times by this many minutes. Use a negative number to move earlier.">Shift times by minutes</FieldLabel>
            <Input id={`bs-shift-${event.id}`} name="shift_minutes" type="number" placeholder="0" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <FieldLabel htmlFor={`bs-notes-${event.id}`} help="Replace the note on every selected slot.">Notes</FieldLabel>
            <Input id={`bs-notes-${event.id}`} name="notes" />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`bs-title-${event.id}`} help="Rename every selected slot to the same title.">Title</FieldLabel>
            <Input id={`bs-title-${event.id}`} name="title" placeholder="Keep" />
          </div>
          <div className="flex flex-wrap items-end gap-2 sm:col-span-4">
            <Button type="submit" disabled={busy}>Apply to slots</Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy || selectedDates.length === 0}
              onClick={() =>
                void run("Slots copied.", () =>
                  bulkCopySlots({
                    data: { ids: selectedSlots, target_date_ids: selectedDates },
                  }),
                )
              }
            >
              <Copy className="mr-1.5 h-4 w-4" /> Copy onto {selectedDates.length} selected date
              {selectedDates.length === 1 ? "" : "s"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                if (!confirm(`Delete ${selectedSlots.length} slots?`)) return;
                void run("Slots deleted.", () => bulkDeleteSlots({ data: { ids: selectedSlots } }));
              }}
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete slots
            </Button>
          </div>
        </form>
      ) : null}

      <ClaimFieldsContext.Provider value={resolveClaimFields((event as any).claim_field_config)}>
      {dates
        .filter((date: AnyRow) => !gapsOnly || dateFill(date).open > 0)
        .map((date: AnyRow) => (
        <DateCard
          key={date.id}
          date={date}
          dates={dates}
          gapsOnly={gapsOnly}
          onChange={onChange}
          selectedDates={selectedDates}
          selectedSlots={selectedSlots}
          onToggleDate={(id) => toggle(selectedDates, setSelectedDates, id)}
          onToggleSlot={(id) => toggle(selectedSlots, setSelectedSlots, id)}
        />
      ))}
      </ClaimFieldsContext.Provider>
      {gapsOnly && dates.every((date: AnyRow) => dateFill(date).open === 0) ? (
        <p className="t-meta">Every slot on this event is claimed.</p>
      ) : null}
    </div>
  );
}

type SelectionProps = {
  selectedDates: string[];
  selectedSlots: string[];
  onToggleDate: (id: string) => void;
  onToggleSlot: (id: string) => void;
};

function DateCard({
  date,
  dates,
  gapsOnly,
  onChange,
  selectedDates,
  selectedSlots,
  onToggleDate,
  onToggleSlot,
}: {
  date: AnyRow;
  dates: AnyRow[];
  gapsOnly?: boolean;
  onChange: () => void;
} & SelectionProps) {
  const fill = dateFill(date);
  const deleteDate = useServerFn(adminDeleteEventDate);
  const updateDate = useServerFn(adminUpdateEventDate);
  const duplicateDate = useServerFn(adminDuplicateEventDate);
  const [mode, setMode] = useState<"view" | "edit" | "duplicate">("view");
  const [busy, setBusy] = useState(false);

  async function submitEdit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await updateDate({
        data: {
          id: date.id,
          event_date: String(form.get("event_date") ?? ""),
          note: String(form.get("note") ?? ""),
        },
      });
      toast.success("Date updated.");
      setMode("view");
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submitEdit" });
      toast.error(error instanceof Error ? error.message : "Could not update that date.");
    } finally {
      setBusy(false);
    }
  }

  async function submitDuplicate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    try {
      const result = await duplicateDate({
        data: {
          id: date.id,
          event_date: String(form.get("event_date") ?? ""),
          note: String(form.get("note") ?? ""),
          copy_slots: form.get("copy_slots") === "on",
        },
      });
      toast.success(
        result.slots > 0 ? `Date copied with ${result.slots} slots.` : "Date copied with no slots.",
      );
      setMode("view");
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submitDuplicate" });
      toast.error(error instanceof Error ? error.message : "Could not copy that date.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id={`date-${date.id}`} className="rounded-sm border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm font-bold tracking-wide text-ice">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={selectedDates.includes(date.id)}
            onChange={() => onToggleDate(date.id)}
            aria-label={`Select ${dayLabel(date.event_date)}`}
          />
          {dayLabel(date.event_date)}
          {date.note ? <span className="t-meta ml-2 font-normal">{date.note}</span> : null}
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-xs font-semibold text-cyan hover:underline"
            onClick={() => setMode(mode === "edit" ? "view" : "edit")}
          >
            Edit date
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold text-cyan hover:underline"
            onClick={() => setMode(mode === "duplicate" ? "view" : "duplicate")}
          >
            <Copy className="h-3.5 w-3.5" /> Duplicate
          </button>
          <button
            type="button"
            aria-label="Delete date"
            onClick={async () => {
              if (!confirm("Delete this date and its slots?")) return;
              try {
                await deleteDate({ data: { id: date.id } });
                toast.success("Date deleted.");
                onChange();
              } catch (error) {
                reportHandledSync(error, { feature: "admin", action: "submitDuplicate" });
                toast.error(
                  error instanceof Error ? error.message : "Could not delete that date.",
                );
              }
            }}

          >
            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
          </button>
        </div>
      </div>

      {mode === "edit" ? (
        <form onSubmit={submitEdit} className="mt-3 grid gap-3 rounded-sm border border-border p-3 sm:grid-cols-3">
          <div className="space-y-2">
            <FieldLabel htmlFor={`edit-date-${date.id}`} help="Change the calendar day for this date and every slot under it.">Date</FieldLabel>
            <Input id={`edit-date-${date.id}`} name="event_date" type="date" defaultValue={date.event_date} required />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <FieldLabel htmlFor={`edit-note-${date.id}`} help="Optional note volunteers see for this day. Example: rain date, or setup day only.">Day note</FieldLabel>
            <Input id={`edit-note-${date.id}`} name="note" defaultValue={date.note ?? ""} />
          </div>
          <div className="flex gap-2 sm:col-span-3">
            <Button type="submit" disabled={busy}>Save date</Button>
            <Button type="button" variant="outline" onClick={() => setMode("view")}>Cancel</Button>
          </div>
        </form>
      ) : null}

      {mode === "duplicate" ? (
        <form onSubmit={submitDuplicate} className="mt-3 grid gap-3 rounded-sm border border-border p-3 sm:grid-cols-3">
          <div className="space-y-2">
            <FieldLabel htmlFor={`dup-date-${date.id}`} help="The new calendar day to copy this date onto.">New date</FieldLabel>
            <Input id={`dup-date-${date.id}`} name="event_date" type="date" defaultValue={date.event_date} required />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <FieldLabel htmlFor={`dup-note-${date.id}`} help="Note for the new day. Leave blank to reuse the note from this day.">Day note</FieldLabel>
            <Input id={`dup-note-${date.id}`} name="note" defaultValue={date.note ?? ""} />
          </div>
          <label className="flex items-center gap-2 text-sm text-ice/80 sm:col-span-3">
            <input type="checkbox" name="copy_slots" defaultChecked className="h-4 w-4" />
            Copy the {(date.event_slots ?? []).length} slots too. Uncheck to create an empty date.
          </label>
          <div className="flex gap-2 sm:col-span-3">
            <Button type="submit" disabled={busy}>Create copy</Button>
            <Button type="button" variant="outline" onClick={() => setMode("view")}>Cancel</Button>
          </div>
        </form>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <CoverageBar fill={fill} className="max-w-[260px] flex-1" />
        <span className="t-meta">
          {fill.capacity === 0
            ? "No slots yet"
            : `${fill.filled} of ${fill.capacity} claimed · ${fill.open} open`}
        </span>
        <FillTag fill={fill} />
      </div>

      <Slots
        date={date}
        dates={dates}
        gapsOnly={gapsOnly}
        onChange={onChange}
        selectedDates={selectedDates}
        selectedSlots={selectedSlots}
        onToggleDate={onToggleDate}
        onToggleSlot={onToggleSlot}
      />
    </div>
  );
}


function Slots({
  date,
  dates,
  gapsOnly,
  onChange,
  selectedSlots,
  onToggleSlot,
}: {
  date: AnyRow;
  dates: AnyRow[];
  gapsOnly?: boolean;
  onChange: () => void;
} & SelectionProps) {
  const saveSlot = useServerFn(adminSaveSlot);
  const deleteSlot = useServerFn(adminDeleteSlot);
  const cancelSignup = useServerFn(adminCancelSignup);
  const [adding, setAdding] = useState(false);

  const slots = [...(date.event_slots ?? [])]
    .filter((slot: AnyRow) => !gapsOnly || slotFill(slot).open > 0)
    .sort((a: AnyRow, b: AnyRow) => {
    const timeCompare = String(a.start_time ?? "").localeCompare(String(b.start_time ?? ""));
    if (timeCompare !== 0) return timeCompare;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });

  async function submitSlot(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    try {
      await saveSlot({
        data: {
          event_date_id: date.id,
          title: String(form.get("title") ?? ""),
          slot_type: String(form.get("slot_type") ?? "shift"),
          start_time: String(form.get("start_time") ?? ""),
          end_time: String(form.get("end_time") ?? ""),
          capacity: Number(form.get("capacity") ?? 1),
          notes: String(form.get("notes") ?? ""),
          sort_order: slots.length,
        },
      });
      setAdding(false);
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submitSlot" });
      toast.error(error instanceof Error ? error.message : "Could not save that slot.");
    }
  }

  return (
    <div className="mt-4 space-y-3">
      {slots.map((slot: AnyRow) => (
        <SlotCard
          key={slot.id}
          slot={slot}
          date={date}
          dates={dates}
          onChange={onChange}
          selected={selectedSlots.includes(slot.id)}
          onToggleSelected={() => onToggleSlot(slot.id)}
          onDelete={async () => {
            if (!confirm("Delete this slot?")) return;
            try {
              await deleteSlot({ data: { id: slot.id } });
              toast.success("Slot deleted.");
              onChange();
            } catch (error) {
              reportHandledSync(error, { feature: "admin", action: "submitSlot" });
              toast.error(error instanceof Error ? error.message : "Could not delete that slot.");
            }
          }}
          onCancelSignup={async (id: string) => {
            if (
              !confirm(
                "Release this person's spot? The slot re-opens and the notify list is emailed.",
              )
            )
              return;
            try {
              await cancelSignup({
                data: {
                  id,
                  origin: typeof window !== "undefined" ? window.location.origin : undefined,
                },
              });
              toast.success("Spot released.");
              onChange();
            } catch (error) {
              reportHandledSync(error, { feature: "admin", action: "submitSlot" });
              toast.error(error instanceof Error ? error.message : "Could not release that spot.");
            }
          }}

        />
      ))}



      {adding ? (
        <form onSubmit={submitSlot} className="space-y-3 rounded-sm border border-border p-3">
          <SlotFields idPrefix={`new-${date.id}`} />
          <div className="flex gap-2">
            <Button type="submit">Save slot</Button>
            <Button type="button" variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button type="button" variant="outline" onClick={() => setAdding(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Add slot
        </Button>
      )}
    </div>
  );
}

function SlotFields({ idPrefix, slot }: { idPrefix: string; slot?: AnyRow }) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-2 lg:col-span-2">
          <FieldLabel htmlFor={`slot-title-${idPrefix}`} help="What volunteers are signing up for. Example: Cannon crew, Setup team, or Bring a case of water.">Slot title</FieldLabel>
          <Input id={`slot-title-${idPrefix}`} name="title" defaultValue={slot?.title ?? ""} required />
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`slot-type-${idPrefix}`} help="Shift means a time block someone works. Item to bring means a supply donation, and volunteers can claim more than one.">Type</FieldLabel>
          <select
            id={`slot-type-${idPrefix}`}
            name="slot_type"
            defaultValue={slot?.slot_type ?? "shift"}
            className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
          >
            <option value="shift">Shift</option>
            <option value="item">Item to bring</option>
          </select>
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`slot-start-${idPrefix}`} help="Time the shift begins. Leave blank for item slots.">Start</FieldLabel>
          <Input id={`slot-start-${idPrefix}`} name="start_time" type="time" defaultValue={(slot?.start_time ?? "").slice(0, 5)} />
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`slot-end-${idPrefix}`} help="Time the shift ends. Leave blank for item slots.">End</FieldLabel>
          <Input id={`slot-end-${idPrefix}`} name="end_time" type="time" defaultValue={(slot?.end_time ?? "").slice(0, 5)} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="space-y-2">
          <FieldLabel htmlFor={`slot-cap-${idPrefix}`} help="How many volunteers or items you need for this slot. The slot shows as full once that many are claimed.">Spots needed</FieldLabel>
          <Input
            id={`slot-cap-${idPrefix}`}
            name="capacity"
            type="number"
            min={1}
            defaultValue={slot?.capacity ?? 1}
          />
        </div>
        <div className="space-y-2 sm:col-span-3">
          <FieldLabel htmlFor={`slot-notes-${idPrefix}`} help="Optional detail volunteers see under the slot. Example: meet at the cannon trailer, wear closed toe shoes.">Notes</FieldLabel>
          <Input id={`slot-notes-${idPrefix}`} name="notes" defaultValue={slot?.notes ?? ""} />
        </div>
      </div>
    </>
  );
}

function SlotCard({
  slot,
  date,
  dates,
  onChange,
  onDelete,
  onCancelSignup,
  selected,
  onToggleSelected,
}: {
  slot: AnyRow;
  date: AnyRow;
  dates: AnyRow[];
  onChange: () => void;
  onDelete: () => void;
  onCancelSignup: (id: string) => void;
  selected: boolean;
  onToggleSelected: () => void;
}) {
  const saveSlot = useServerFn(adminSaveSlot);
  const duplicateSlot = useServerFn(adminDuplicateSlot);
  const [mode, setMode] = useState<"view" | "edit" | "duplicate">("view");
  const [busy, setBusy] = useState(false);

  const signups = (slot.event_signups ?? []).filter((s: AnyRow) => s.status === "confirmed");
  const filled = signups.reduce((sum: number, s: AnyRow) => sum + Number(s.quantity ?? 1), 0);

  async function submitEdit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await saveSlot({
        data: {
          id: slot.id,
          title: String(form.get("title") ?? ""),
          slot_type: String(form.get("slot_type") ?? "shift"),
          start_time: String(form.get("start_time") ?? ""),
          end_time: String(form.get("end_time") ?? ""),
          capacity: Number(form.get("capacity") ?? 1),
          notes: String(form.get("notes") ?? ""),
          sort_order: Number(slot.sort_order ?? 0),
        },
      });
      toast.success("Slot updated.");
      setMode("view");
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submitEdit" });
      toast.error(error instanceof Error ? error.message : "Could not update that slot.");
    } finally {
      setBusy(false);
    }
  }

  async function submitDuplicate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    try {
      const result = await duplicateSlot({
        data: {
          id: slot.id,
          target_date_id: String(form.get("target_date_id") ?? date.id),
          times: Number(form.get("times") ?? 1),
        },
      });
      toast.success(`Created ${result.count} copy${result.count > 1 ? "s" : ""}.`);
      setMode("view");
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submitDuplicate" });
      toast.error(error instanceof Error ? error.message : "Could not copy that slot.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-sm bg-muted/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Select slot ${slot.title}`}
          />
          <span>
            <span className="block text-sm font-semibold text-ice">{slot.title}</span>
            <span className="t-meta block">
              {slot.slot_type === "item" ? "Item" : "Shift"}
              {slot.start_time ? ` · ${formatTime(slot.start_time)}` : ""}
              {slot.end_time ? ` to ${formatTime(slot.end_time)}` : ""} · {filled} of {slot.capacity} filled
            </span>
            <span className="mt-1.5 flex flex-wrap items-center gap-2">
              <CapacityPips slot={slot} />
              <FillTag fill={slotFill(slot)} />
            </span>
          </span>
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-xs font-semibold text-cyan hover:underline"
            onClick={() => setMode(mode === "edit" ? "view" : "edit")}
          >
            Edit slot
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold text-cyan hover:underline"
            onClick={() => setMode(mode === "duplicate" ? "view" : "duplicate")}
          >
            <Copy className="h-3.5 w-3.5" /> Duplicate
          </button>
          <button type="button" aria-label="Delete slot" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
          </button>
        </div>
      </div>

      {mode === "edit" ? (
        <form onSubmit={submitEdit} className="mt-3 space-y-3 rounded-sm border border-border p-3">
          <SlotFields idPrefix={`edit-${slot.id}`} slot={slot} />
          <div className="flex gap-2">
            <Button type="submit" disabled={busy}>Save changes</Button>
            <Button type="button" variant="outline" onClick={() => setMode("view")}>Cancel</Button>
          </div>
        </form>
      ) : null}

      {mode === "duplicate" ? (
        <form onSubmit={submitDuplicate} className="mt-3 grid gap-3 rounded-sm border border-border p-3 sm:grid-cols-3">
          <div className="space-y-2 sm:col-span-2">
            <FieldLabel htmlFor={`dupslot-date-${slot.id}`} help="Which day the copy lands on. Defaults to this same day.">Copy onto</FieldLabel>
            <select
              id={`dupslot-date-${slot.id}`}
              name="target_date_id"
              defaultValue={date.id}
              className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
            >
              {dates.map((d: AnyRow) => (
                <option key={d.id} value={d.id}>
                  {dayLabel(d.event_date)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor={`dupslot-times-${slot.id}`} help="How many copies to create. Handy for repeating the same shift several times.">How many copies</FieldLabel>
            <Input id={`dupslot-times-${slot.id}`} name="times" type="number" min={1} max={20} defaultValue={1} />
          </div>
          <div className="flex gap-2 sm:col-span-3">
            <Button type="submit" disabled={busy}>Create copies</Button>
            <Button type="button" variant="outline" onClick={() => setMode("view")}>Cancel</Button>
          </div>
        </form>
      ) : null}

      {signups.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {signups.map((signup: AnyRow) => (
            <SignupRow
              key={signup.id}
              signup={signup}
              slot={slot}
              onChange={onChange}
              onCancelSignup={onCancelSignup}
            />
          ))}
        </ul>
      ) : null}
      <AddSignup slot={slot} full={filled >= Number(slot.capacity)} onChange={onChange} />
    </div>
  );
}


function SignupRow({
  signup,
  slot,
  onChange,
  onCancelSignup,
}: {
  signup: AnyRow;
  slot: AnyRow;
  onChange: () => void;
  onCancelSignup: (id: string) => void;
}) {
  const updateSignup = useServerFn(adminUpdateSignup);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await updateSignup({
        data: {
          id: signup.id,
          full_name: String(form.get("full_name") ?? ""),
          email: String(form.get("email") ?? ""),
          phone: String(form.get("phone") ?? ""),
          quantity: Number(form.get("quantity") ?? signup.quantity ?? 1),
          note: String(form.get("note") ?? ""),
          t1d_connection: String(form.get("t1d_connection") ?? ""),
          referral_source: String(form.get("referral_source") ?? ""),
          referral_name: String(form.get("referral_name") ?? ""),
        },
      });
      toast.success("Signup updated.");
      setEditing(false);
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submit" });
      toast.error(error instanceof Error ? error.message : "Could not update that signup.");
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <li>
        <form onSubmit={submit} className="panel grid gap-3 p-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel htmlFor={`ed-name-${signup.id}`} help="Fix spelling or capitalization of their name.">Full name</FieldLabel>
            <Input id={`ed-name-${signup.id}`} name="full_name" defaultValue={signup.full_name ?? ""} required maxLength={120} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor={`ed-email-${signup.id}`} help="Where their confirmation and reminders go.">Email</FieldLabel>
            <Input id={`ed-email-${signup.id}`} name="email" type="email" defaultValue={signup.email ?? ""} maxLength={255} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor={`ed-phone-${signup.id}`} help="Optional day-of contact number.">Phone</FieldLabel>
            <Input id={`ed-phone-${signup.id}`} name="phone" defaultValue={signup.phone ?? ""} maxLength={40} />
          </div>
          {slot.slot_type === "item" ? (
            <div className="space-y-1.5">
              <FieldLabel htmlFor={`ed-qty-${signup.id}`} help="How many of this item they are bringing.">Quantity</FieldLabel>
              <Input id={`ed-qty-${signup.id}`} name="quantity" type="number" min={1} max={100} defaultValue={Number(signup.quantity ?? 1)} />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <FieldLabel htmlFor={`ed-t1d-${signup.id}`} help="Their relationship to Type 1 Diabetes.">T1D connection</FieldLabel>
            <select
              id={`ed-t1d-${signup.id}`}
              name="t1d_connection"
              defaultValue={signup.t1d_connection ?? ""}
              className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
            >
              <option value="">Not set</option>
              {T1D_RELATIONSHIPS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor={`ed-src-${signup.id}`} help="How they heard about the event.">Heard via</FieldLabel>
            <select
              id={`ed-src-${signup.id}`}
              name="referral_source"
              defaultValue={signup.referral_source ?? ""}
              className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
            >
              <option value="">Not set</option>
              {REFERRAL_SOURCES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor={`ed-ref-${signup.id}`} help="Who referred them, if anyone.">Referred by</FieldLabel>
            <Input id={`ed-ref-${signup.id}`} name="referral_name" defaultValue={signup.referral_name ?? ""} maxLength={160} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <FieldLabel htmlFor={`ed-note-${signup.id}`} help="Anything you want on the record for this signup.">Note</FieldLabel>
            <Input id={`ed-note-${signup.id}`} name="note" defaultValue={signup.note ?? ""} maxLength={1000} />
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" size="sm" disabled={busy}>
              {busy ? "Saving..." : "Save changes"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </li>
    );
  }

  const meta = [
    signup.t1d_connection ? String(signup.t1d_connection) : null,
    signup.referral_source ? `heard via ${signup.referral_source}` : null,
    signup.referral_name ? `referred by ${signup.referral_name}` : null,
    signup.quantity > 1 ? `x${signup.quantity}` : null,
  ].filter(Boolean) as string[];

  return (
    <li className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-border/50 py-2 last:border-b-0">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-sm font-semibold">{signup.full_name}</p>
        <p className="t-meta truncate">
          {[signup.email, signup.phone].filter(Boolean).join(" · ")}
        </p>
        {meta.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {meta.map((item) => (
              <span
                key={item}
                className="rounded-sm bg-muted px-1.5 py-0.5 text-[11px] leading-4 text-muted-foreground"
              >
                {item}
              </span>
            ))}
          </div>
        ) : null}
        {signup.note ? <p className="t-meta italic">{signup.note}</p> : null}
      </div>
      <span className="flex shrink-0 gap-3 pt-0.5">
        <button
          type="button"
          className="text-xs font-semibold text-muted-foreground hover:text-foreground"
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
        <button
          type="button"
          className="text-xs font-semibold text-muted-foreground hover:text-destructive"
          onClick={() => onCancelSignup(signup.id)}
        >
          Release
        </button>
      </span>
    </li>
  );
}


function AddSignup({
  slot,
  full,
  onChange,
}: {
  slot: AnyRow;
  full: boolean;
  onChange: () => void;
}) {
  const addSignup = useServerFn(adminAddSignup);
  const claimFields = useContext(ClaimFieldsContext);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setSaving(true);
    const claim_answers: Record<string, unknown> = {};
    for (const field of claimFields) {
      claim_answers[field.key] =
        field.type === "checkbox"
          ? form.get(`cf_${field.key}`) === "on"
          : String(form.get(`cf_${field.key}`) ?? "");
    }
    try {
      await addSignup({
        data: {
          slot_id: slot.id,
          claim_answers,
          full_name: String(form.get("full_name") ?? ""),
          email: String(form.get("email") ?? ""),
          phone: String(form.get("phone") ?? ""),
          quantity: Number(form.get("quantity") ?? 1),
          note: String(form.get("note") ?? ""),
          t1d_connection: String(form.get("t1d_connection") ?? ""),
          referral_source: String(form.get("referral_source") ?? ""),
          referral_name: String(form.get("referral_name") ?? ""),
          send_email: form.get("send_email") === "on",
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });
      toast.success("Added to this slot.");
      setOpen(false);
      onChange();
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "submit" });
      toast.error(error instanceof Error ? error.message : "Could not add that person.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="mt-3 text-xs font-semibold text-cyan hover:underline disabled:opacity-50"
        disabled={full}
        onClick={() => setOpen(true)}
      >
        {full ? "Slot is full" : "+ Add someone to this slot"}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-sm border border-border p-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2">
          <FieldLabel htmlFor={`su-name-${slot.id}`} help="Full name of the person you are placing in this slot.">Name</FieldLabel>
          <Input id={`su-name-${slot.id}`} name="full_name" required />
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`su-email-${slot.id}`} help="Their email address. Leave blank if you only have a phone number.">Email</FieldLabel>
          <Input id={`su-email-${slot.id}`} name="email" type="email" />
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`su-phone-${slot.id}`} help="Optional phone number for day of contact.">Phone</FieldLabel>
          <Input id={`su-phone-${slot.id}`} name="phone" />
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`su-qty-${slot.id}`} help="How many they are covering. Shifts are always one person.">Quantity</FieldLabel>
          <Input
            id={`su-qty-${slot.id}`}
            name="quantity"
            type="number"
            min={1}
            defaultValue={1}
            disabled={slot.slot_type !== "item"}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-2">
          <FieldLabel htmlFor={`su-t1d-${slot.id}`} help="Their primary relationship to Type 1 Diabetes.">T1D connection</FieldLabel>
          <select
            id={`su-t1d-${slot.id}`}
            name="t1d_connection"
            defaultValue=""
            className="h-11 w-full rounded-sm border border-input bg-background px-3 text-sm"
          >
            <option value="">Not provided</option>
            {T1D_RELATIONSHIPS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`su-refsrc-${slot.id}`} help="How they heard about this event.">How did they hear about it</FieldLabel>
          <select
            id={`su-refsrc-${slot.id}`}
            name="referral_source"
            defaultValue=""
            className="h-11 w-full rounded-sm border border-input bg-background px-3 text-sm"
          >
            <option value="">Not provided</option>
            {REFERRAL_SOURCES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor={`su-refname-${slot.id}`} help="Who referred them, if anyone.">Referred by</FieldLabel>
          <Input id={`su-refname-${slot.id}`} name="referral_name" maxLength={160} />
        </div>
      </div>
      <AdminClaimFieldInputs slotId={slot.id} />
      <div className="space-y-2">
        <FieldLabel htmlFor={`su-note-${slot.id}`} help="Anything you want on the record for this signup.">Note</FieldLabel>
        <Input id={`su-note-${slot.id}`} name="note" />
      </div>
      <label className="flex items-center gap-2 text-sm text-ice/80">
        <input type="checkbox" name="send_email" className="h-4 w-4" />
        Send them the confirmation email with a manage link
      </label>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? "Adding..." : "Add to slot"}
        </Button>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
